import React, { useEffect, useState, useCallback } from "react";
import {
  Col,
  Row,
  Card,
  CardBody,
  Button,
  InputGroup,
  Input,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Container,
  InputGroupText,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import { Link } from "react-router-dom";
import { Triangle } from "react-loader-spinner";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import CustomDataTable from "../../../Components/CustomTable/CustomTable";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import Flatpickr from "react-flatpickr";

import SalesReportService from "../../../services/OrderingServices/SalesReportService";
import {
  formatDate,
  formatDateTimes,
} from "../../../Components/Common/FormatDate";
import ReturnBill from "../component/ReturnBill";
import SalesReportDetail from "../component/SalesReportDetail";
import { exportToExcel } from "../../../utilis/exportToExcel";
import CountUp from "react-countup";
import NumberWithCommas from "../../../pages/Pages/Starter/NumberWithCommas";

import useTableSearch from "../../../hooks/useTableSearch";
import TableSearch from "../../../Components/Common/TableSearch";

const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const customStyles = {
  headCells: {
    style: {
      fontSize: "0.92rem",
      fontWeight: 610,
    },
  },
  rows: {
    style: {
      fontSize: "0.9rem",
    },
  },
};

const SalesReport = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addModal, setAddModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState();
  const navigate = useNavigate();
  const [deleteModal, setDeleteModal] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const today = new Date().toISOString().split("T")[0];
  const [dateRange, setDateRange] = useState([]);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [totalOrderCount, setTotalOrderCount] = useState(0);
  const [totalDiscountAmount, settotalDiscountAmount] = useState(0);
  const [totalSalesAmount, setTotalSalesAmount] = useState(0);
  const [actualSalesAmount, setActualSalesAmount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [filters, setFilters] = useState({
    BillDateFrom: "",
    BillDateTo: "",
  });
  const [debouncedFilters, setDebouncedFilters] = useState(filters);

  // Export modal states
  const [exportModal, setExportModal] = useState(false);
  const [exportDateRange, setExportDateRange] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);

  const toggleDetailModal = (row) => {
    setIsDetailOpen(!isDetailOpen);
    setSelectedItem(row);
  };

  const columns = [
    {
      name: "S.N",
      selector: (row, index) => (currentPage - 1) * perPage + index + 1,
      width: "75px",
    },
    {
      name: "Invoice No",
      selector: (row) => `${row.invoiceNumber}`,
      cell: (row) => row.invoiceNumber,
      sortable: true,
    },
    {
      name: "Order Ref Id",
      selector: (row) => (
        <Link to={`/order/${row?.orderReferenceId}`}>
          {row.orderReferenceId}
        </Link>
      ),
      sortable: true,
      width: "15vw",
    },
    {
      name: "Bill Date",
      selector: (row) => formatDateTimes(row.billDate),
      sortable: true,
      width: "14vw",
    },
    {
      name: "Discount(%)",
      selector: (row) => row.discountPercentage,
      sortable: true,
      right: true,
      width: "10vw",
    },
    {
      name: "Total",
      selector: (row) => <NumberWithCommas number={row.grandTotal} />,
      sortable: true,
      right: true,
    },
    {
      name: "Action",
      selector: (row) => (
        <>
          <Button
            color="btn btn-soft-success"
            className="btn-sm"
            onClick={() => toggleDetailModal(row)}
          >
            <i className="bx bx-show" />
          </Button>

          {!row.isReturned && (
            <Button
              className="btn btn-soft-danger btn-sm ms-1"
              color="danger"
              onClick={() => handleDeleteClick(row)}
            >
              Return
            </Button>
          )}
        </>
      ),
      center: true,
    },
  ];

  const { searchTerm, setSearchTerm, filteredData } = useTableSearch(data, [
    "invoiceNumber",
    "orderReferenceId",
    "billDate",
    "discountPercentage",
    "grandTotal",
  ]);

  const fetchCustomers = async (
    page = currentPage,
    size = perPage,
    currentFilters = debouncedFilters
  ) => {
    try {
      setLoading(true);
      const response = await SalesReportService.getList({
        pageNumber: page,
        perPage: size,
        BillDateFrom: currentFilters.BillDateFrom,
        BillDateTo: currentFilters.BillDateTo,
      });

      setData(response?.items || []);
      setTotalOrderCount(response?.summary?.totalOrderCount);
      settotalDiscountAmount(response?.summary?.totalDiscountAmount);
      setTotalSalesAmount(response?.summary?.totalSalesAmount);
      setActualSalesAmount(response?.summary?.actualSalesAmount);
      setTotalRows(response?.totalCount || 0);

      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log("🚀 ~ error:", error);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePerRowsChange = (newPerPage, page) => {
    setPerPage(newPerPage);
    setCurrentPage(page);
  };

  // Default: last 1 month on mount
  useEffect(() => {
    const today = new Date();
    const lastMonth = new Date();
    lastMonth.setMonth(today.getMonth() - 1);

    setFromDate(lastMonth);
    setToDate(today);
    setDateRange([lastMonth, today]);

    // Set initial filters
    setFilters({
      BillDateFrom: formatLocalDate(lastMonth),
      BillDateTo: formatLocalDate(today),
    });
  }, []);

  // Debounce filter changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedFilters(filters);
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [filters]);

  // Fetch items when page, pageSize, or debouncedFilters change
  useEffect(() => {
    if (debouncedFilters.BillDateFrom && debouncedFilters.BillDateTo) {
      fetchCustomers(currentPage, perPage);
    }
  }, [currentPage, perPage, debouncedFilters]);

  // FIXED: Handle date range change
  const handleDateRangeChange = (dates) => {
    if (dates.length === 2) {
      setDateRange(dates);
      setFromDate(dates[0]);
      setToDate(dates[1]);

      // Update filters to trigger data fetch
      setFilters({
        BillDateFrom: formatLocalDate(dates[0]),
        BillDateTo: formatLocalDate(dates[1]),
      });
    }
  };

  const toggleDeleteModal = () => setDeleteModal(!deleteModal);

  const handleDeleteClick = (item) => {
    setSelectedItem(item);
    toggleDeleteModal();
  };

  const toggleExportModal = () => {
    setExportModal(!exportModal);
    if (!exportModal) {
      setExportDateRange([]);
    }
  };

  // Handle export with date range
  const handleExportWithDateRange = async () => {
    if (!fromDate || !toDate) {
      toast.error("Please select a valid date range");
      return;
    }

    try {
      setExportLoading(true);

      const startDate = formatLocalDate(fromDate);
      const endDate = formatLocalDate(toDate);

      const response = await SalesReportService.getList({
        pageNumber: 1,
        perPage: 10000,
        BillDateFrom: startDate,
        BillDateTo: endDate,
      });

      const exportData = response?.items || [];

      if (exportData.length === 0) {
        toast.error("No data found for the selected date range");
        setExportLoading(false);
        return;
      }

      const columnMapping = {
        "S.N": (row, index) => index + 1,
        "Invoice No.": "invoiceNumber",
        "Order Reference Id": "orderReferenceId",
        "Bill Date": (row) => formatDate(row.billDate),
        "Discount (%)": "discountPercentage",
        "Total Amount": "grandTotal",
      };

      exportToExcel(
        exportData,
        columnMapping,
        `SalesReport_${startDate}_to_${endDate}.xlsx`
      );

      toast.success("Export successful!");
      setExportLoading(false);
      toggleExportModal();
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export data");
      setExportLoading(false);
    }
  };

  const SummaryCard = ({ title, value, color, icon }) => (
    <Card className="shadow-sm border-0">
      <CardBody className="d-flex align-items-center justify-content-between">
        <div className="w-100">
          <h6 className="fw-semibold text-uppercase" style={{ color }}>
            {title}
          </h6>
          <div className="d-flex justify-content-between w-100">
            <h4 className="fs-2 fw-bold" style={{ color }}>
              <NumberWithCommas number={value} />
            </h4>
            <div
              className="rounded-circle d-flex align-items-center justify-content-center"
              style={{
                backgroundColor: color + "20",
                color,
                width: "36px",
                height: "36px",
              }}
            >
              <i className={`${icon} fs-5`} />
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  );

  return (
    <Container fluid>
      <div className="page-content">
        <BreadCrumb title="Sales Report" pageTitle="Reports" />
        <Row className="">
          <Col lg={3} md={6} sm={12}>
            <SummaryCard
              title="Total Orders"
              value={totalOrderCount}
              color="#0d6efd"
              icon="bx bx-receipt"
            />
          </Col>
          <Col lg={3} md={6} sm={12}>
            <SummaryCard
              title="Total Discount"
              value={totalDiscountAmount}
              color="#ffc107"
              icon="bx bx-purchase-tag"
            />
          </Col>
          <Col lg={3} md={6} sm={12}>
            <SummaryCard
              title="Total Sales"
              value={totalSalesAmount}
              color="#28a745"
              icon="bx bx-dollar-circle"
            />
          </Col>
          <Col lg={3} md={6} sm={12}>
            <SummaryCard
              title="Total Actual Sales"
              value={actualSalesAmount}
              color="#28a745"
              icon="bx bx-dollar-circle"
            />
          </Col>
        </Row>

        <div
          className="p-3 p-lg-4 "
          style={{
            background: "#ffffff",
            borderRadius: "0.5rem",
            boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
            border: "1px solid #eee",
          }}
        >
          <Row className="justify-content-between align-items-center mb-3">
            {/* Title */}
            <Col md={4}>
              <h5 className="mb-1 fw-bold text-dark">Sales Report List</h5>
            </Col>

            {/* Filters and Export */}
            <Col md={8}>
              <Row className="justify-content-end align-items-center g-2">
                {/* Table Search */}
                <Col md={4}>
                  <TableSearch
                    value={searchTerm}
                    onChange={setSearchTerm}
                    placeholder="Search by all.."
                  />
                </Col>

                {/* Date Range */}
                <Col md={4}>
                  <InputGroup>
                    <Flatpickr
                      options={{ mode: "range", dateFormat: "Y-m-d" }}
                      value={dateRange}
                      onChange={handleDateRangeChange}
                      placeholder="Select Date Range"
                      className="form-control"
                    />
                    <InputGroupText style={{ cursor: "pointer" }}>
                      <i className="bx bx-calendar"></i>
                    </InputGroupText>
                  </InputGroup>
                </Col>

                {/* Export Button */}
                <Col md="auto">
                  <Button
                    color="success"
                    onClick={handleExportWithDateRange}
                    className="d-flex align-items-center gap-2"
                    style={{ borderRadius: "8px" }}
                    disabled={exportLoading}
                  >
                    <i className="bx bx-export"></i>
                    {exportLoading ? "Exporting..." : "Export"}
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>

          <hr style={{ borderColor: "#000000ff", marginTop: "0rem" }} />
          <div className="d-none d-lg-block">
            <CustomDataTable
              responsive
              striped
              pagination
              fixedHeader
              persistTableHead
              progressPending={loading}
              customStyles={customStyles}
              paginationServer
              paginationTotalRows={totalRows}
              onChangeRowsPerPage={handlePerRowsChange}
              onChangePage={handlePageChange}
              paginationDefaultPage={currentPage}
              paginationPerPage={perPage}
              progressComponent={
                <div className="my-3">
                  <Triangle
                    visible={true}
                    height="80"
                    width="80"
                    color="#5B71B9"
                    ariaLabel="triangle-loading"
                  />
                  <h5 className="mt-1">Loading...</h5>
                </div>
              }
              columns={columns}
              data={filteredData}
              noDataComponent={
                <div className="text-center my-5 w-100">
                  <i className="ri-emotion-unhappy-line display-4 text-secondary" />
                  <h5 className="fw-semibold text-secondary">
                    No data Available
                  </h5>
                </div>
              }
            />
          </div>
        </div>

        {loading ? (
          <div className="my-5 d-flex justify-content-center d-md-none d-lg-none d-block">
            <Triangle
              visible={true}
              height="60"
              width="60"
              color="#5B71B9"
              ariaLabel="triangle-loading"
            />
          </div>
        ) : (
          <>
            {filteredData?.map((item, index) => (
              <Card
                key={item.id || index}
                className="mb-3 shadow-sm d-block d-lg-none d-md-none mt-2"
              >
                <CardBody>
                  <Row>
                    <Col xs={12}>
                      <div className="d-flex justify-content-between align-content-center align-items-center">
                        <Link
                          to={`/order/${item?.orderReferenceId}`}
                          className="cursor-pointer "
                        >
                          <h6 className="mb-2 text-primary">
                            {item?.orderReferenceId}
                          </h6>
                        </Link>
                        <div>
                          <Dropdown
                            isOpen={item.dropdownOpen || false}
                            toggle={(e) => {
                              e.stopPropagation();
                              setData((prev) =>
                                prev.map((customer) =>
                                  customer.id === item.id
                                    ? {
                                        ...customer,
                                        dropdownOpen: !customer.dropdownOpen,
                                      }
                                    : { ...customer, dropdownOpen: false }
                                )
                              );
                            }}
                            direction="down"
                          >
                            <DropdownToggle
                              color="primary"
                              outline
                              className="btn-sm  shadow-sm rounded-circle"
                            >
                              <i className="ri-more-2-fill"></i>
                            </DropdownToggle>
                            <DropdownMenu end>
                              <DropdownItem
                                onClick={() => toggleDetailModal(item)}
                              >
                                <i className="bx bx-show text-success me-2" />{" "}
                                View
                              </DropdownItem>
                              <DropdownItem
                                onClick={() => handleDeleteClick(item.id)}
                              >
                                <i className="ri-delete-bin-5-line text-danger me-2"></i>{" "}
                                Delete
                              </DropdownItem>
                            </DropdownMenu>
                          </Dropdown>
                        </div>
                      </div>
                      <h6 className="mb-2">
                        {formatDateTimes(item?.billDate)}
                      </h6>
                      <h6 className="mb-2 d-flex justify-content-between align-content-center align-items-center">
                        <strong>Invoice No</strong>{" "}
                        {item?.invoiceNumber || "none"}
                      </h6>
                      <h6 className="mb-2 d-flex justify-content-between align-content-center align-items-center">
                        <strong>Discount (%)</strong>{" "}
                        {item?.discountPercentage || 0}
                      </h6>
                      <h6 className="mb-0 d-flex justify-content-between align-content-center align-items-center fw-bold">
                        <strong>Total</strong> {item?.grandTotal || 0}
                      </h6>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            ))}
          </>
        )}

        <ReturnBill
          show={deleteModal}
          onCloseClick={toggleDeleteModal}
          selectedItem={selectedItem}
          setSelectedItem={setSelectedItem}
        />

        <SalesReportDetail
          isOpen={isDetailOpen}
          toggle={toggleDetailModal}
          selectedItem={selectedItem}
        />
      </div>
    </Container>
  );
};

export default SalesReport;
